import { RelatedProductsDocument } from "./../../graphql/Product.mjs";
import {
  extractConfigurableProductListingOptions,
  resolveNewInRangeFilterOption
} from "./utils.mjs";
import { useSsrCoreApp } from "../ssrControl.mjs";
import { useAuth } from "../auth.mjs";
import {
  CrossSellProductsDocument,
  DiscountedProductsDocument,
  ProductsDocument,
  SearchProductsDocument
} from "../../graphql/Product.mjs";
import { extractValue, toNonNullable } from "../../utils/collections.mjs";
import {
  resolvePricePerStep,
  resolveProductPrice,
  resolveProductStock
} from "../../utils/product.mjs";
import {
  CustomerCartCrossSellProductsDocument,
  GuestCartCrossSellProductsDocument
} from "../../graphql/Cart.mjs";
import { useStoredCart } from "../../composables/cart/cart.mjs";
import { uniqBy } from "lodash-es";
import { useQuery } from "villus";
import { computed, onMounted, ref, watch } from "vue";
const replaceReducer = (_, curr) => {
  return curr;
};
export const appendReducer = (old, curr) => {
  return uniqBy([...old, ...curr], "id");
};
export function useProductsGeneric(variables, type = "default", opts = {
  fetchOnMount: true,
  newIn: 0
}) {
  const defaults = { fetchOnMount: true, newIn: 0 };
  const { fetchOnMount, newIn } = { ...defaults, ...opts };
  const products = ref([]);
  const totalCount = ref(0);
  const variablesInput = computed(() => {
    const realValues = extractValue(variables);
    if (newIn) {
      return {
        ...realValues,
        filter: {
          ...realValues?.filter,
          ...resolveNewInRangeFilterOption(newIn)
        }
      };
    }
    return realValues;
  });
  const { data, error, isFetching } = useQuery({
    query: resolveProductTypeQuery(type),
    variables: variablesInput,
    fetchOnMount
  });
  watch(data, (value) => {
    const fetchedPage = data.value?.connection?.page_info?.current_page;
    const reducer = fetchedPage === 1 || !fetchedPage ? replaceReducer : appendReducer;
    products.value = reducer(
      products.value,
      toNonNullable(value?.connection?.nodes).map(mapProductListing)
    );
    totalCount.value = value?.connection?.total_count || 0;
  });
  return {
    products,
    totalCount,
    data,
    error,
    isFetching
  };
}
export function useProducts(variables, opts) {
  const products = ref([]);
  const totalCount = ref(0);
  const { execute, data, error, isFetching } = useQuery({
    query: ProductsDocument,
    variables,
    fetchOnMount: opts?.fetchOnMount ?? true
  });
  watch(data, (value) => {
    const fetchedPage = data.value?.connection?.page_info?.current_page;
    const reducer = fetchedPage === 1 || !fetchedPage ? replaceReducer : appendReducer;
    products.value = reducer(
      products.value,
      toNonNullable(value?.connection?.nodes).map(mapProductListing)
    );
    totalCount.value = value?.connection?.total_count || 0;
  });
  return {
    execute,
    products,
    totalCount,
    data,
    error,
    isFetching
  };
}
export function useDiscountedProducts(variables, opts) {
  const products = ref([]);
  const totalCount = ref(0);
  const { data, error, isFetching } = useQuery({
    query: DiscountedProductsDocument,
    variables,
    fetchOnMount: opts?.fetchOnMount || true
  });
  watch(data, (value) => {
    const fetchedPage = data.value?.connection?.page_info?.current_page;
    const reducer = fetchedPage === 1 || !fetchedPage ? replaceReducer : appendReducer;
    products.value = reducer(
      products.value,
      toNonNullable(value?.connection?.nodes).map(mapProductListing)
    );
    totalCount.value = value?.connection?.total_count || 0;
  });
  return {
    products,
    totalCount,
    data,
    error,
    isFetching
  };
}
export function useSearchProducts(variables, opts) {
  const products = ref([]);
  const categoriesResult = ref([]);
  const { data, error, isFetching, execute } = useQuery({
    query: SearchProductsDocument,
    variables,
    fetchOnMount: opts?.fetchOnMount ?? true
  });
  watch(data, (value) => {
    products.value = value?.search?.catalogsearch_fulltext?.items?.map(mapProductListing).slice(0, 5);
    categoriesResult.value = value?.search?.magento_catalog_category?.items;
  });
  return {
    products,
    isFetching,
    error,
    execute,
    categoriesResult
  };
}
export async function useCrossSellProducts(urlKey) {
  const { execute } = useQuery({
    query: CrossSellProductsDocument,
    variables: {
      slug: urlKey
    },
    fetchOnMount: false
  });
  const response = await execute();
  return response?.data?.product?.items?.filter((item) => item)?.map((item) => mapProductListing(item)) || [];
}
const fetchCustomerCartCrossSellProducts = async () => {
  const { execute } = useQuery({
    query: CustomerCartCrossSellProductsDocument,
    cachePolicy: "network-only",
    fetchOnMount: false
  });
  const { data } = await execute();
  return data?.cart?.crossSell?.items?.filter((item) => !!item)?.map(mapProductListing);
};
const fetchGuestCartCrossSellProducts = async (cartId) => {
  const { execute } = useQuery({
    query: GuestCartCrossSellProductsDocument,
    variables: { cartId },
    cachePolicy: "network-only",
    fetchOnMount: false
  });
  const { data } = await execute();
  return data?.cart?.crossSell?.items?.filter((item) => !!item)?.map(mapProductListing);
};
const useCustomerCartCrossSellProducts = async (_fetchCustomerCartCrossSellProducts = fetchCustomerCartCrossSellProducts) => {
  return await _fetchCustomerCartCrossSellProducts();
};
const useGuestCartCrossSellProducts = async (cartId, _fetchGuestCartCrossSellProducts = fetchGuestCartCrossSellProducts) => {
  return await _fetchGuestCartCrossSellProducts(cartId);
};
export function useCartCrossProducts() {
  const { isLoggedIn } = useAuth();
  const { cartState } = useStoredCart();
  const { waitForPageToBeLoaded } = useSsrCoreApp();
  const items = ref([]);
  onMounted(async () => {
    await waitForPageToBeLoaded();
    items.value = await (isLoggedIn.value ? await useCustomerCartCrossSellProducts() : useGuestCartCrossSellProducts(cartState.value?.cartId ?? "")) || [];
  });
  return { items: computed(() => items.value) };
}
export async function useRelatedProducts(urlKey) {
  const { execute } = useQuery({
    query: RelatedProductsDocument,
    variables: {
      slug: urlKey
    },
    fetchOnMount: false
  });
  const response = await execute();
  return response?.data?.product?.items?.filter((item) => item)?.map((item) => mapProductListing(item)) || [];
}
export function mapProductListing(apiProduct) {
  const isNew = apiProduct.attributes?.find((attribute) => attribute?.key === "is_new")?.value === "Yes";
  const cartControl = {
    max: Number(apiProduct.cart_control.max_amount) ?? Number.MAX_VALUE,
    // maximum quantity allowed in the cart
    min: Number(apiProduct.cart_control.min_amount) ?? 0,
    // minimum quantity allowed in the cart
    step: Number(apiProduct.cart_control.increment_step) ?? 1
    // quantity step
  };
  const productsVariants = apiProduct.__typename === "ConfigurableProduct" ? apiProduct.variants?.map((variant) => ({
    ...variant?.product,
    price: variant?.product && resolveProductPrice(variant?.product) || 0,
    priceBefore: function() {
      const currentPrice2 = variant?.product && resolveProductPrice(variant?.product) || 0;
      const priceBefore2 = variant?.product?.price_range.maximum_price?.regular_price.value ?? currentPrice2;
      return currentPrice2 < priceBefore2 ? priceBefore2 : void 0;
    }()
  })) : [];
  const currentPrice = resolveProductPrice(apiProduct);
  const oldPrice = apiProduct.price_range.maximum_price?.regular_price.value ?? currentPrice;
  const pricePerStep = computed(
    () => resolvePricePerStep(currentPrice, Number(cartControl?.step))
  );
  const beforePricePerStep = computed(
    () => resolvePricePerStep(oldPrice, Number(cartControl?.step))
  );
  const price = computed(() => {
    if (["GroupedProduct", "ConfigurableProduct"].includes(apiProduct.__typename)) {
      return productsVariants?.reduce(
        (accu, option) => option.price < accu ? option.price : accu,
        Number.MAX_SAFE_INTEGER
      );
    }
    if (pricePerStep.value && pricePerStep.value !== currentPrice) {
      return pricePerStep.value;
    }
    return currentPrice;
  });
  const priceBefore = computed(() => {
    if (["GroupedProduct", "ConfigurableProduct"].includes(apiProduct.__typename)) {
      return productsVariants?.reduce(
        (accu, option) => option?.price < accu?.price ? option : accu,
        {
          price: Number.MAX_SAFE_INTEGER,
          priceBefore: 0
        }
      )?.priceBefore;
    }
    if (beforePricePerStep.value && beforePricePerStep.value !== oldPrice) {
      return pricePerStep.value;
    }
    return oldPrice;
  });
  return {
    id: apiProduct.id || "",
    name: apiProduct.name || "",
    sku: apiProduct.sku || "",
    isNew,
    // TODO: Remove this when product listing is extendable
    isBestseller: apiProduct.is_best_seller,
    stock: resolveProductStock(apiProduct) || 0,
    price: price.value || 0,
    priceBefore: priceBefore.value ?? 0,
    slug: apiProduct.url_key || "",
    thumb: {
      src: apiProduct.thumbnail?.url || "",
      alt: apiProduct.thumbnail?.label || apiProduct.name || ""
    },
    categories: apiProduct.categories,
    attributes: apiProduct.attributes,
    type: apiProduct.__typename,
    cartControl,
    options: extractConfigurableProductListingOptions(apiProduct),
    productsVariants,
    brand: apiProduct.brand
  };
}
export function resolveProductTypeQuery(type) {
  if (type === "discounted") {
    return DiscountedProductsDocument;
  }
  if (type === "default") {
    return ProductsDocument;
  }
  return ProductsDocument;
}
